import { toCanvas } from 'html-to-image';

/**
 * Базовый снимок Стола: видимая область экрана средствами фронта.
 *
 * Механика: снимается ПЕРВЫЙ ребёнок рамки #stol-screen-root (корень страницы,
 * полная высота), затем канвас кропается до видимого окна рамки (scrollTop +
 * clientHeight) — прокрутка страницы попадает в кадр честно. Известное
 * ограничение захода 1: прокрутка ВЛОЖЕННЫХ списков в клоне DOM сбрасывается
 * к началу (свойство клонирующих библиотек) — паспорт несёт числа отдельно.
 */
export async function captureVisibleScreen(): Promise<Blob | null> {
  const out = await captureVisibleCanvas();
  if (!out) {
    return null;
  }

  return await new Promise<Blob | null>((resolve) => out.toBlob(resolve, 'image/png'));
}

/**
 * Тот же снимок холстом — для тех, кому нужно дорисовать поверх до отправки
 * (техподдержка `s737` закрывает на нём телефоны клиентов и обводит место сбоя).
 * Холст — ровно видимое окно рамки, точка в точку с экраном.
 */
export async function captureVisibleCanvas(): Promise<HTMLCanvasElement | null> {
  const wrapper = document.getElementById('stol-screen-root');
  if (!wrapper) {
    return null;
  }
  const target = (wrapper.firstElementChild as HTMLElement | null) ?? wrapper;

  const canvas = await toCanvas(target, {
    pixelRatio: 1,
    backgroundColor: '#F5F6FA',
    // Кросс-доменные картинки (аватарки с api.opsmind.ru) без CORS-заголовка
    // роняли весь снимок — их не тянем, а любой прочий сбой картинки заменяется
    // прозрачным пикселем вместо ошибки.
    filter: (node) => {
      if (node instanceof HTMLImageElement && node.src) {
        try {
          return new URL(node.src, window.location.href).origin === window.location.origin;
        } catch {
          return false;
        }
      }
      return true;
    },
    imagePlaceholder:
      'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
  });

  const y = Math.min(wrapper.scrollTop, Math.max(0, canvas.height - 1));
  const w = Math.min(wrapper.clientWidth, canvas.width);
  const h = Math.min(wrapper.clientHeight, canvas.height - y);

  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  if (!ctx) {
    return null;
  }
  ctx.drawImage(canvas, 0, y, w, h, 0, 0, w, h);

  return out;
}
